import { Decimal as DecimalJs } from 'decimal.js'

export class Decimal {
    /**
     * 创建一个 DecimalJs 实例
     * @param value - 输入值，可以是字符串或数字
     * @returns 返回 DecimalJs 实例
     * @throws 如果输入值不是字符串或数字，抛出错误
     */
    private createDecimal(value: string | number): DecimalJs {
        if (value === 'undefined' || (typeof value !== 'string' && typeof value !== 'number')) {
            throw new Error('Invalid input type. Expected string or number.')
        }
        return new DecimalJs(value || 0)
    }

    /**
     * 加法运算
     * @param args - 任意数量的操作数，可以是字符串或数字
     * @returns 返回所有参数相加的结果（数字类型）
     */
    plus(...args: Array<string | number>): number {
        if (!args || args.length === 0) {
            return 0
        }
        return args
            .map((arg) => this.createDecimal(String(arg) || 0)) // 将每个参数转换为 DecimalJs 实例
            .reduce((sum, current) => sum.plus(current), new DecimalJs(0)) // 累加
            .toNumber() // 转换为数字
    }

    /**
     * 减法运算
     * @param a - 第一个操作数，可以是字符串或数字
     * @param b - 第二个操作数，可以是字符串或数字
     * @returns 返回减法运算的结果（数字类型）
     */
    minus(a: string | number, b: string | number): number {
        return this.createDecimal(a).minus(this.createDecimal(b)).toNumber()
    }

    /**
     * 乘法运算
     * @param a - 第一个操作数，可以是字符串或数字
     * @param b - 第二个操作数，可以是字符串或数字
     * @returns 返回乘法运算的结果（数字类型）
     */
    multiply(a: string | number, b: string | number): number {
        return this.createDecimal(a).mul(this.createDecimal(b)).toNumber()
    }

    /**
     * 除法运算
     * @param a - 第一个操作数，可以是字符串或数字
     * @param b - 第二个操作数，可以是字符串或数字
     * @returns 返回除法运算的结果（数字类型）
     * @throws 如果除数为 0，抛出错误
     */
    divide(a: string | number, b: string | number): number {
        const divisor = this.createDecimal(b)
        if (divisor.equals(0)) {
            throw new Error('Division by zero is not allowed.')
        }
        return this.createDecimal(a).div(divisor).toNumber()
    }

    toFixed(num: number, digit = 2) {
        return this.createDecimal(num).toFixed(digit)
    }
}

export const decimal = new Decimal()
